import { calculateValue } from 'checkout/selectors/payment/generic'
import { ExperienceOfferPageState } from 'contexts/Experience/ExperienceOfferPage/experienceOfferPageStateReducer'
import { arrayToMap, nonNullable } from 'lib/array/arrayUtils'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { SavedExperienceData } from 'tripPlanner/types/tripItem'
import { CreateExperienceRequestBody } from 'tripPlanner/utils/itemConversions/common'

interface Params {
  experience: App.ExperienceOffer
  currentRegionCode: string
  currencyCode: string
  experienceOfferPageState?: ExperienceOfferPageState
  allTicketOptions: Array<App.ExperienceItemTicket>
  templateId?: string
  templateItemId?: string
}

function hasValidTicketSelections(
  allTicketOptions: Array<App.ExperienceItemTicket>,
  experienceOfferPageState: ExperienceOfferPageState,
) {
  if (!experienceOfferPageState) {
    return false
  }

  const ticketIds = Object.keys(experienceOfferPageState.ticketCounts).filter(
    (id) => experienceOfferPageState.ticketCounts[id] > 0,
  )
  const ticketOptionsById = arrayToMap(allTicketOptions, (option) => option.id)
  const haveTicketData = (id: string) => ticketOptionsById.has(id)

  return ticketIds.length > 0 && ticketIds.every(haveTicketData)
}

function hasSomeDataToSave(
  experience: App.ExperienceOffer,
  allTicketOptions: Array<App.ExperienceItemTicket>,
  experienceOfferPageState?: ExperienceOfferPageState,
) {
  if (!experienceOfferPageState) {
    return false // No data to save
  }
  if (experience.hasCalendar) {
    return !!experienceOfferPageState.date // Can at least save the date
  }
  return hasValidTicketSelections(allTicketOptions, experienceOfferPageState) // Non-calendar experience has ticket selections
}

export function createExperienceItemPayload({
  experience,
  currentRegionCode,
  currencyCode,
  experienceOfferPageState,
  allTicketOptions,
  templateId,
  templateItemId,
}: Params): CreateExperienceRequestBody {
  const date = experience.hasCalendar ?
    experienceOfferPageState?.date :
    undefined
  const time = experience.hasTimeslots ?
    experienceOfferPageState?.time :
    undefined

  const location =
    experienceOfferPageState?.redemptionLocation ?? experience.location

  let place: CreateExperienceRequestBody['place'] = {
    type: 'CUSTOM',
    name: location.name,
  }

  const { latitude, longitude } = location

  if (typeof latitude === 'number' && typeof longitude === 'number') {
    place = {
      ...place,
      type: 'GEO',
      point: {
        lat: latitude,
        lng: longitude,
      },
    }
  }

  const payload: CreateExperienceRequestBody = {
    type: 'EXPERIENCE',
    isBooked: false,
    name: experience.name,
    sourceType: 'LE_Experience',
    startDate: date,
    endDate: date,
    startTime: time,
    place,
    code: experience.id,
    savedItemData: {
      regionCode: currentRegionCode,
      currencyCode,
      price: experience.price,
      value: experience.price,
      experienceId: experience.id,
      leExclusive: experience.leExclusive,
      imageIds: nonNullable(experience.images.map((image) => image.id)),
    },
    templateId,
    templateItemId,
  }

  if (
    experienceOfferPageState &&
    hasSomeDataToSave(experience, allTicketOptions, experienceOfferPageState)
  ) {
    // Format restore cart data

    let savedItemData: SavedExperienceData = {
      regionCode: currentRegionCode,
      experienceId: experience.id,
      currencyCode,
      date: experienceOfferPageState.date || '',
      languageId: experienceOfferPageState.language?.id,
      time: experienceOfferPageState.time,
      pickupPointId: experienceOfferPageState.pickupPoint?.id,
      redemptionLocationId: experienceOfferPageState.redemptionLocation?.id,
      price: experience.price,
      value: calculateValue(experience.price, experience.discount),
      discountPercent: experience.discount,
      leExclusive: experience.leExclusive,
      imageIds: nonNullable(experience.images.map((image) => image.id)),
    }

    if (hasValidTicketSelections(allTicketOptions, experienceOfferPageState)) {
      // Reset price/value so we can add them up properly from the ticket selections
      let price = 0
      let value = 0
      const tickets: SavedExperienceData['tickets'] = []

      Object.entries(experienceOfferPageState.ticketCounts).forEach(
        ([ticketId, count]) => {
          if (count === 0) {
            return
          }

          const ticket = allTicketOptions.find(
            (ticket) => ticket.id === ticketId,
          )

          if (!ticket) {
            throw new Error('Ticket was not in the allTicketOptions array')
          }

          tickets.push({
            ticketId,
            count,
            fareType: ticket.fareType,
            name: ticket.name,
          })

          price += ticket.price * count
          value += calculateValue(ticket.price, ticket.discount) * count
        },
      )

      const discountPercent = getValueOffPercent(value, price)

      // This gives us enough data to restore cart the user all the way to checkout.
      // So upgrade the restore cart data from `experience-bookmark` type to `experience`.
      savedItemData = {
        ...savedItemData,
        price,
        value,
        discountPercent,
        tickets,
      }
    }

    payload.savedItemData = savedItemData
  }

  return payload
}
